import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import { type Customer, customerService } from '../services/customerService';
import {
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { CustomerDrawer } from './CustomerDrawer';

type PanelMode = 'empty' | 'view' | 'edit' | 'add';

export const CustomerManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);

  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getAll(),
    refetchOnMount: 'always',
  });

  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedRow, setSelectedRow] = useState<Customer | null>(null);
  const [editingRow, setEditingRow] = useState<Customer | null>(null);

  const handleAdd = () => {
    setEditingRow(null);
    setPanelMode('add');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await customerService.delete(id);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setSelectedRow(null);
      setPanelMode('empty');
    } catch (err) {
      toast.error(
        `Failed to delete customer: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  const handleRowClicked = useCallback((event: RowClickedEvent<Customer>) => {
    if (!event.data) return;
    setSelectedRow(event.data);
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleAddClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    setPanelMode('empty');
    setEditingRow(null);
  }, []);

  const handleEditClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingRow(null);
      return;
    }
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedRow(null);
  }, []);

  const columnDefs: ColDef<Customer>[] = useMemo(
    () => [
      {
        field: 'customer_id',
        headerName: 'ID',
        width: 140,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'customer_name',
        headerName: 'Name',
        flex: 1,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'city',
        headerName: 'City',
        width: 140,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'country',
        headerName: 'Country',
        width: 100,
        valueFormatter: (p) => p.value || '-',
      },
    ],
    []
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <div className="p-2">
      <style>{agGridThemeStyles}</style>
      <SettingsListDetailLayout
        detailOpen={detailOpen}
        detailVariant="default"
        detail={
          panelMode === 'add' ? (
            <CustomerDrawer
              isOpen={true}
              onClose={handleAddClose}
              onSave={() => {}}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['customers'] });
                setPanelMode('empty');
                setSelectedRow(null);
                setEditingRow(null);
              }}
              row={null}
              isEdit={false}
            />
          ) : panelMode === 'edit' && editingRow ? (
            <CustomerDrawer
              isOpen={true}
              onClose={handleEditClose}
              onSave={(row) => setSelectedRow(row)}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['customers'] });
                setPanelMode('view');
                setEditingRow(null);
              }}
              row={editingRow}
              isEdit
            />
          ) : panelMode === 'view' && selectedRow ? (
            <CustomerDrawer
              key={selectedRow.id}
              isOpen={true}
              readOnly
              row={selectedRow}
              onClose={handleViewClose}
              onSave={() => {}}
              onEditRequest={() => {
                setEditingRow(selectedRow);
                setPanelMode('edit');
              }}
              onDeleteRequest={() => void handleDelete(selectedRow.id)}
            />
          ) : null
        }
        list={
          <>
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h2 className="m-0 text-2xl font-semibold text-primary">Customers</h2>
              <Button type="button" onClick={handleAdd}>
                <PlusIcon data-icon="inline-start" />
                Add Customer
              </Button>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-4 p-8 text-text-secondary">
                <Spinner className="size-10" />
                <p>Loading customers...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center p-8 text-danger-600">
                <p>Error loading data: {error.message}</p>
                <Button type="button" className="mt-4" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <p className="p-8 text-center text-text-secondary">
                No customers found. Add your first customer to get started.
              </p>
            ) : (
              <div
                className="ag-theme-material min-h-0 w-full flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  theme={agGridTheme}
                  rowData={rows}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  domLayout="normal"
                  rowNumbers
                  getRowId={(params) => String(params.data.id)}
                />
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
